import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import './Home.css';

// Google Orchestrator
function Home({ user }) {
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const animationFrameRef = useRef(null);
  const lastButtonStates = useRef({});
  const connectedGamepad = useRef(null);

  // Gamepad navigation for Home page
  const handleGamepadButton = useCallback(
    (buttonIndex) => {
      // Log ALL button presses for debugging
      const buttonNames = [
        'A',
        'B',
        'X',
        'Y',
        'LB',
        'RB',
        'LT',
        'RT',
        'Back',
        'Start',
        'L3',
        'R3',
        'D-Up',
        'D-Down',
        'D-Left',
        'D-Right',
        'Xbox',
      ];
      console.log(
        `🎮 Button ${buttonIndex} (${
          buttonNames[buttonIndex] || 'Unknown'
        }) pressed`
      );

      switch (buttonIndex) {
        case 0: // A - Go to Library/Login
          if (user) {
            navigate('/library');
          } else {
            navigate('/login');
          }
          break;
        case 9: // Start - Go to Library
          if (user) {
            navigate('/library');
          } else {
            navigate('/login');
          }
          break;
        case 12: // D-pad Up
          console.log('🎮 D-pad Up');
          break;
        case 13: // D-pad Down
          console.log('🎮 D-pad Down');
          break;
        case 14: // D-pad Left
          console.log('🎮 D-pad Left');
          break;
        case 15: // D-pad Right
          console.log('🎮 D-pad Right');
          break;
        default:
          break;
      }
    },
    [navigate, user]
  );

  // Poll gamepad
  const pollGamepad = useCallback(() => {
    const gamepads = navigator.getGamepads();
    const gamepad = gamepads[connectedGamepad.current];

    if (gamepad) {
      // Check buttons
      gamepad.buttons.forEach((button, index) => {
        const isPressed = button.pressed;
        const wasPressed = lastButtonStates.current[index] || false;

        if (isPressed && !wasPressed) {
          handleGamepadButton(index);
        }

        lastButtonStates.current[index] = isPressed;
      });

      // Check joystick axes (0=Left X, 1=Left Y, 2=Right X, 3=Right Y)
      const threshold = 0.5;
      const axes = gamepad.axes;

      // Left stick
      if (Math.abs(axes[0]) > threshold || Math.abs(axes[1]) > threshold) {
        const direction =
          Math.abs(axes[0]) > Math.abs(axes[1])
            ? axes[0] > 0
              ? 'Right'
              : 'Left'
            : axes[1] > 0
            ? 'Down'
            : 'Up';
        if (!lastButtonStates.current['leftStick' + direction]) {
          console.log(`🎮 Left Stick: ${direction}`);
          lastButtonStates.current['leftStick' + direction] = true;
        }
      } else {
        lastButtonStates.current['leftStickUp'] = false;
        lastButtonStates.current['leftStickDown'] = false;
        lastButtonStates.current['leftStickLeft'] = false;
        lastButtonStates.current['leftStickRight'] = false;
      }
    }

    animationFrameRef.current = requestAnimationFrame(pollGamepad);
  }, [handleGamepadButton]);

  // Debug: Dump full gamepad state
  const dumpGamepadState = useCallback(() => {
    const gamepads = navigator.getGamepads();
    const gamepad = gamepads[connectedGamepad.current];
    if (gamepad) {
      console.log('🎮 === FULL GAMEPAD STATE ===');
      console.log('ID:', gamepad.id);
      console.log('Mapping:', gamepad.mapping);
      console.log('Buttons:', gamepad.buttons.length);
      gamepad.buttons.forEach((btn, i) => {
        if (btn.pressed || btn.value > 0.1) {
          console.log(
            `  Button ${i}: pressed=${btn.pressed}, value=${btn.value.toFixed(
              2
            )}`
          );
        }
      });
      console.log('Axes:', gamepad.axes.length);
      gamepad.axes.forEach((axis, i) => {
        if (Math.abs(axis) > 0.1) {
          console.log(`  Axis ${i}: ${axis.toFixed(2)}`);
        }
      });
      console.log('=============================');
    }
  }, []);

  // Setup gamepad listeners
  useEffect(() => {
    let dumpInterval = null;

    const handleGamepadConnected = (e) => {
      console.log('🎮 Home: Gamepad connected:', e.gamepad.id);
      console.log('🎮 Buttons available:', e.gamepad.buttons.length);
      console.log('🎮 Axes available:', e.gamepad.axes.length);
      console.log('🎮 Mapping:', e.gamepad.mapping);
      connectedGamepad.current = e.gamepad.index;
      lastButtonStates.current = {};
      animationFrameRef.current = requestAnimationFrame(pollGamepad);

      // Dump state every 2 seconds for debugging
      if (dumpInterval) {
        clearInterval(dumpInterval);
      }
      dumpInterval = setInterval(() => dumpGamepadState(), 2000);
    };

    const handleGamepadDisconnected = (e) => {
      console.log('🎮 Home: Gamepad disconnected');
      if (connectedGamepad.current === e.gamepad.index) {
        connectedGamepad.current = null;
        cancelAnimationFrame(animationFrameRef.current);
        if (dumpInterval) {
          clearInterval(dumpInterval);
          dumpInterval = null;
        }
      }
    };

    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);

    // Check for existing gamepad
    const gamepads = navigator.getGamepads();
    for (let i = 0; i < gamepads.length; i++) {
      if (gamepads[i]) {
        connectedGamepad.current = i;
        animationFrameRef.current = requestAnimationFrame(pollGamepad);
        break;
      }
    }

    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener(
        'gamepaddisconnected',
        handleGamepadDisconnected
      );
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (dumpInterval) {
        clearInterval(dumpInterval);
      }
    };
  }, [pollGamepad, dumpGamepadState]);

  return (
    <div className="home">
      <div className="container">
        <div className="hero">
          <Logo size={80} showText={false} className="hero-logo" />
          <h1 className="hero-title">Google Orchestrator</h1>
          <p className="hero-subtitle">
            Your unified music hub - all your streaming services in one place
          </p>
          <div className="hero-buttons">
            {user ? (
              <button
                className="button button-primary"
                onClick={() => navigate('/library')}
              >
                Go to Library
              </button>
            ) : (
              <>
                <button
                  className="button button-primary"
                  onClick={() => navigate('/login')}
                >
                  Get Started
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </button>
              </>
            )}
          </div>
        </div>

        {user && (
          <div className="admin-tools-section">
            <h2 className="section-title">🛠️ Admin Tools</h2>
            <div className="tools-grid">
              <a
                href="http://localhost:3001"
                target="_blank"
                rel="noopener noreferrer"
                className="tool-link"
              >
                <div className="tool-title">🎵 Metadata Update Manager</div>
                <div className="tool-description">
                  Search MusicBrainz, match albums, preview changes, and update
                  metadata
                </div>
                <div className="tool-note">
                  Opens in new tab • Requires metadata-gui-server.js running
                </div>
              </a>
            </div>
          </div>
        )}

        <div className="features">
          <div className="feature-card">
            <div className="feature-icon">🎵</div>
            <h3>Unified Library</h3>
            <p>YouTube, Spotify, Apple Music - all in one place</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔗</div>
            <h3>Connect Services</h3>
            <p>Link your streaming accounts with one click</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎛️</div>
            <h3>One Controller</h3>
            <p>Play, search, and manage all your music seamlessly</p>
          </div>
        </div>
      </div>
      {previewOpen && (
        <div className="preview-mode">
          <button
            className="close-button"
            onClick={() => setPreviewOpen(false)}
          >
            Close
          </button>
          <h2>Preview Mode</h2>
          {/* Preview mode content goes here */}
        </div>
      )}
    </div>
  );
}

export default Home;
